import { 
  Box, Paper, Typography, Button, Alert, 
  Container, CircularProgress, Link
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import AuthService from '../services/AuthService';
import { log } from '../utils/errors/logger';

const VerifyEmail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState('');
  
  useEffect(() => {
    const verifyToken = async () => {
      // Extract token from URL query parameters
      const query = new URLSearchParams(location.search);
      const token = query.get('token');
      
      if (!token) {
        setError(t('verifyEmail.noToken', 'Kein Verifizierungstoken gefunden.'));
        return;
      }
      
      setVerifying(true);
      
      try {
        await AuthService.verifyEmail(token);
        setVerified(true);
        setError('');
      } catch (error) {
        log.error('Verification error', { error: error instanceof Error ? error.message : String(error) });
        setError((error as Error)?.message || t('verifyEmail.error', 'Verifizierung fehlgeschlagen.'));
      } finally {
        setVerifying(false);
      }
    };
    
    verifyToken();
  }, [location, t]);
  
  const handleGoToLogin = () => {
    navigate('/login');
  };
  
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
      }}
    >
      <Container component="main" maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Typography component="h1" variant="h4" color="primary" gutterBottom>
            {t('verifyEmail.title', 'E-Mail-Verifizierung')}
          </Typography>
          
          {verifying && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 4 }}>
              <CircularProgress size={60} />
              <Typography variant="body1" sx={{ mt: 2 }}>
                {t('verifyEmail.verifying', 'Verifiziere deine E-Mail-Adresse...')}
              </Typography>
            </Box>
          )}
          
          {!verifying && error && (
            <Alert severity="error" sx={{ my: 2 }}>
              {error}
            </Alert>
          )}
          
          {!verifying && verified && (
            <>
              <Alert severity="success" sx={{ my: 2 }}>
                {t('verifyEmail.success', 'Deine E-Mail-Adresse wurde erfolgreich verifiziert!')}
              </Alert>
              
              <Typography variant="body1" sx={{ mb: 3 }}>
                {t('verifyEmail.successMessage', 'Du kannst dich jetzt mit deinen Zugangsdaten anmelden.')}
              </Typography>
            </>
          )}
          
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleGoToLogin}
            disabled={verifying}
            sx={{ mt: 2 }}
          >
            {t('verifyEmail.goToLogin', 'Zur Anmeldeseite')}
          </Button>
          
          {error && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" color="text.secondary">
                {t('verifyEmail.problem', 'Probleme bei der Verifizierung?')}
              </Typography>
              <Link 
                href="/resend-verification" 
                underline="hover"
                sx={{ cursor: 'pointer' }}
              >
                {t('verifyEmail.resend', 'Verifizierungs-E-Mail erneut senden')}
              </Link>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default VerifyEmail;
